/**
 * Given an amount in cents (integer), return the string representation.
 * `prefix` is added to this function to improve the readability of common usage
 * (without the nested template literal).
 * @example
 * formatCents(order.total, '$') // "$2.00"
 * formatCents(order.total)      // "2.00"
 */
export const formatCents = (cents: number | null | undefined, prefix = '') => {
  if (cents === null || cents === undefined) return '';
  return `${prefix}${(cents / 100).toFixed(2)}`;
};

/**
 * Calculate usage cost in cents: units * unitPrice (cents), rounded to nearest cent.
 */
export const calcUsageCostCents = (units: number | null | undefined, unitPrice: number): number =>
  Math.round((units ?? 0) * unitPrice);

/**
 * Converts a value from <Input> (string) and converts it to cents.
 * @param value - Supports `unknown` so it can be used as a Zod preprocess function.
 * @example
 *  '2.00' (string) => 200 (number)
 */
export const parseCents = (value: unknown): number | undefined => {
  // Bulk edit returns `null` value if the field is empty.
  if (value === null) return undefined;

  if (typeof value === 'string') {
    // Text inputs with no value (ie ""), should be undefined (ie unset).
    if (value === '') return undefined;

    // Avoid raising error when '.' is entered as input.
    if (value === '.') return 0;

    return Math.round(Number.parseFloat(value.replace(/[^\d.]/g, '')) * 100);
  }

  // Pass through all other values (including `number` untouched).
  if (typeof value === 'number') {
    return value;
  }

  throw new Error(`convertToCents has invalid value type: ${typeof value}`);
};
